import { useState } from 'react'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import { annexCompleteness, ANNEX_FIELD_COUNT } from '@/lib/migration/engine'
import { archivedAnnexes, inScope } from '@/lib/migration/summary'
import { Chip, Icon } from '@/components/readiness/primitives'
import { Bar } from '@/components/migration/primitives'

// Plan rail (I3 §10.2): Part A with its six section dots, one row per annex
// with completeness chip and bar (drag to reorder; the order is the export
// order), the amber "CII systems without an annex" block, and Add annex.

export type RailSelection = { part: 'a'; section?: string } | { annex: string }

export function PlanRail({ ctx, selected, onSelect }: { ctx: MigrationCtx; selected: RailSelection; onSelect: (s: RailSelection) => void }) {
  const { plan: data, actions, summary } = ctx
  const [menu, setMenu] = useState(false)
  const [picker, setPicker] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  if (!data.plan || !summary) return null
  // Archived annexes sit in their own collapsed list, out of Part B's order.
  const annexes = data.annexes.filter((a) => !a.archived_at)
  const archived = archivedAnnexes(data.annexes)
  const isA = 'part' in selected
  const selAnnex = 'annex' in selected ? selected.annex : null
  const systems = data.systems
  const addSystem = async (id: string) => {
    setPicker(false)
    setMenu(false)
    const a = await actions.addAnnexFromSystem(id).catch(() => null)
    if (a) onSelect({ annex: a.id })
  }
  const drop = (targetId: string) => {
    if (!dragId || dragId === targetId) return
    const ids = annexes.map((a) => a.id)
    const from = ids.indexOf(dragId)
    const to = ids.indexOf(targetId)
    ids.splice(from, 1)
    ids.splice(to, 0, dragId)
    // The reorder lists every annex; archived ones keep their place at the end.
    void actions.reorderAnnexes([...ids, ...archived.map((a) => a.id)])
    setDragId(null)
  }

  return (
    <aside className="w-full shrink-0 space-y-4 rounded-[12px] p-3 lg:w-[300px]" style={{ background: 'var(--rd-bg-tile)' }} aria-label="Plan sections" data-testid="plan-rail">
      {/* Part A */}
      <section>
        <p className="rd-label px-1">Part A · Organisation <span className="rd-mono normal-case tracking-normal" style={{ color: 'var(--rd-text-secondary)' }}>{summary.part_a.answered} / 6</span></p>
        <button type="button" onClick={() => onSelect({ part: 'a' })} className="mt-1.5 w-full rounded-[10px] p-2.5 text-left" style={{ background: isA ? 'var(--rd-bg-card)' : 'transparent', border: `1px solid ${isA ? 'var(--rd-accent-primary)' : 'transparent'}` }} aria-pressed={isA} data-testid="rail-part-a">
          <div className="flex items-center justify-between gap-2 text-[14px] font-medium">
            <span>Organisation level</span>
            {summary.part_a.first_open ? <Chip tone="warn">Section {summary.part_a.first_open.n} open</Chip> : <Chip tone="ok">complete</Chip>}
          </div>
          <Bar value={summary.part_a.answered} max={6} className="mt-2" color={summary.part_a.answered === 6 ? 'var(--rd-status-ok)' : 'var(--rd-status-warn)'} />
        </button>
        <ul className="mt-1.5 space-y-0.5 px-1">
          {summary.part_a.sections.map((s) => (
            <li key={s.key}>
              <button type="button" className="flex w-full items-center gap-2 rounded px-1.5 py-0.5 text-left text-[12.5px] hover:underline" onClick={() => onSelect({ part: 'a', section: s.key })} style={{ color: 'var(--rd-text-secondary)' }}>
                <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: s.answered ? 'var(--rd-status-ok)' : 'var(--rd-status-warn)' }} aria-label={s.answered ? 'complete' : 'open'} />
                <span className="rd-mono" style={{ color: 'var(--rd-text-faint)' }}>{s.n}.</span> <span className="truncate">{s.title}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Part B */}
      <section>
        <p className="rd-label px-1">Part B · System annexes <span className="rd-mono normal-case tracking-normal" style={{ color: 'var(--rd-text-secondary)' }}>{summary.cii_covered} / {summary.cii_count} CII covered · {inScope(annexes).length} total</span></p>
        <ul className="mt-1.5 space-y-1" data-testid="rail-annexes">
          {annexes.map((a) => {
            const c = annexCompleteness(a, a.blocks)
            const sel = selAnnex === a.id
            const tone = a.no_longer_cii ? 'neutral' : c.answered === ANNEX_FIELD_COUNT ? 'ok' : c.answered === 0 ? 'neutral' : 'info'
            return (
              <li key={a.id} draggable onDragStart={() => setDragId(a.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(a.id)} onDragEnd={() => setDragId(null)} className="group" style={{ opacity: dragId === a.id ? 0.5 : a.no_longer_cii ? 0.7 : 1 }}>
                <button type="button" onClick={() => onSelect({ annex: a.id })} className="w-full rounded-[10px] p-2.5 text-left" style={{ background: sel ? 'var(--rd-bg-card)' : 'transparent', border: `1px solid ${sel ? 'var(--rd-accent-primary)' : 'transparent'}` }} aria-pressed={sel} data-testid={`rail-annex-${a.id}`}>
                  <div className="flex items-center gap-2 text-[14px] font-medium">
                    <span className="hidden shrink-0 cursor-grab group-hover:inline" style={{ color: 'var(--rd-text-faint)' }} aria-hidden="true">⋮⋮</span>
                    <span className="min-w-0 flex-1 truncate">{a.system_name || 'Untitled annex'}</span>
                    {a.no_longer_cii ? <Chip tone="neutral">no longer CII</Chip> : <Chip tone={tone}><span className="rd-mono">{c.answered}/{c.total}</span></Chip>}
                  </div>
                  <Bar value={c.answered} max={c.total} className="mt-2" color={tone === 'ok' ? 'var(--rd-status-ok)' : 'var(--rd-status-info)'} />
                </button>
              </li>
            )
          })}
          {annexes.length === 0 && <li className="px-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>No annexes yet.</li>}
        </ul>

        {archived.length > 0 && (
          <div className="mt-2 px-1" data-testid="rail-archived">
            <button type="button" className="flex w-full items-center gap-1.5 text-left text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} onClick={() => setShowArchived((v) => !v)} aria-expanded={showArchived} data-testid="rail-archived-toggle">
              <Icon name={showArchived ? 'chevron' : 'chevron-right'} size={12} /> Archived · {archived.length}
            </button>
            {showArchived && (
              <ul className="mt-1 space-y-0.5">
                {archived.map((a) => (
                  <li key={a.id}>
                    <button type="button" onClick={() => onSelect({ annex: a.id })} className="w-full truncate rounded px-2 py-1 text-left text-[12.5px] hover:underline" style={{ color: 'var(--rd-text-secondary)', background: selAnnex === a.id ? 'var(--rd-bg-card)' : 'transparent' }} title={a.archive_reason} data-testid={`rail-archived-${a.id}`}>
                      {a.system_name || 'Untitled annex'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {summary.missing_systems.length > 0 && (
          <div className="mt-3 rounded-[10px] p-2.5" style={{ border: '1px dashed var(--rd-status-warn)', background: 'var(--rd-tint-l1)' }} data-testid="rail-missing">
            <p className="text-[11.5px] font-semibold" style={{ color: 'var(--rd-status-warn)' }}>CII systems without an annex · {summary.missing_systems.length}</p>
            <ul className="mt-1.5 space-y-1">
              {summary.missing_systems.map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-[12.5px]">
                  <span className="min-w-0 flex-1 truncate">{s.name}</span>
                  <span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{s.asset_count} assets</span>
                  <button type="button" className="rd-link" onClick={() => void addSystem(s.id)} data-testid={`rail-add-${s.id}`}>+ Add</button>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px]" style={{ color: 'var(--rd-text-muted)' }}>Flagged as CII in Discovery. Blocking at export.</p>
          </div>
        )}

        <div className="relative mt-3">
          <button type="button" className="rd-btn w-full" onClick={() => setMenu((v) => !v)} aria-haspopup="menu" aria-expanded={menu} data-testid="add-annex">
            <Icon name="plus" size={14} /> Add annex <Icon name="chevron" size={14} />
          </button>
          {menu && (
            <div role="menu" className="rd-card absolute left-0 right-0 top-full z-20 mt-1 p-1 text-[14px]" style={{ boxShadow: 'var(--rd-shadow-float)', border: '1px solid var(--rd-border-control)' }}>
              <button type="button" role="menuitem" className="block w-full rounded px-3 py-1.5 text-left hover:underline" onClick={() => { setPicker(true); setMenu(false) }}>From Discovery…</button>
              <button type="button" role="menuitem" className="block w-full rounded px-3 py-1.5 text-left hover:underline" onClick={async () => { setMenu(false); const a = await actions.addBlankAnnex().catch(() => null); if (a) onSelect({ annex: a.id }) }}>Blank annex</button>
              <button type="button" role="menuitem" className="block w-full rounded px-3 py-1.5 text-left hover:underline disabled:opacity-50" disabled={!selAnnex} onClick={async () => { if (!selAnnex) return; setMenu(false); const a = await actions.duplicateAnnex(selAnnex).catch(() => null); if (a) onSelect({ annex: a.id }) }}>Duplicate selected</button>
            </div>
          )}
          {picker && (
            <div role="listbox" aria-label="Discovery systems" className="rd-card absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto p-1 text-[12.5px]" style={{ boxShadow: 'var(--rd-shadow-float)', border: '1px solid var(--rd-border-control)' }} data-testid="system-picker">
              {[...systems].sort((a, b) => Number(b.cii) - Number(a.cii) || a.name.localeCompare(b.name)).map((s) => {
                const has = annexes.some((a) => a.system_id === s.id)
                return (
                  <button key={s.id} type="button" role="option" aria-selected={false} disabled={has} className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left hover:underline disabled:opacity-50" onClick={() => void addSystem(s.id)}>
                    <span className="min-w-0 flex-1 truncate">{s.name}</span>
                    {s.cii && <Chip tone="chip">CII</Chip>}
                    {has && <span style={{ color: 'var(--rd-text-faint)' }}>added</span>}
                  </button>
                )
              })}
              {systems.length === 0 && <p className="px-3 py-1.5" style={{ color: 'var(--rd-text-muted)' }}>No Discovery systems yet.</p>}
              <button type="button" className="rd-link mt-1 w-full px-3 py-1 text-left" onClick={() => setPicker(false)}>Cancel</button>
            </div>
          )}
        </div>
      </section>
    </aside>
  )
}
